import React, { useState, useRef, useCallback } from 'react';

interface ImageCompareProps {
  beforeUrl: string;
  afterUrl: string;
  beforeLabel?: string;
  afterLabel?: string;
}

export default function ImageCompare({
  beforeUrl,
  afterUrl,
  beforeLabel = 'Before',
  afterLabel = 'After',
}: ImageCompareProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const updatePosition = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (dragging) updatePosition(e.clientX);
  }, [dragging, updatePosition]);

  const onPointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative rounded-xl overflow-hidden cursor-col-resize select-none"
      style={{ backgroundColor: 'var(--sage-surface)' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* After image (full width behind) */}
      <img src={afterUrl} alt={afterLabel} className="w-full h-auto block" draggable={false} />

      {/* Before image (clipped by position) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${position}%` }}
      >
        <img
          src={beforeUrl}
          alt={beforeLabel}
          className="h-full object-cover"
          style={{ width: containerRef.current?.clientWidth ?? '100%' }}
          draggable={false}
        />
      </div>

      {/* Slider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5"
        style={{ left: `${position}%`, backgroundColor: 'white', transform: 'translateX(-50%)' }}
      >
        {/* Slider handle */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
          style={{ backgroundColor: 'white' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M4 2L1 7L4 12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 2L13 7L10 12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3">
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white' }}
        >
          {beforeLabel}
        </span>
      </div>
      <div className="absolute top-3 right-3">
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white' }}
        >
          {afterLabel}
        </span>
      </div>
    </div>
  );
}
